import React from "react";
import { motion } from "framer-motion";
import { Check, Plug, Rocket, Sparkles } from "lucide-react";

const integrationPartners = [
  { name: "Dentrix", logo: "https://framerusercontent.com/images/FK2PxMGUliSNEptVBpWhWGjhW4.png" },
  { name: "Eaglesoft", logo: "/assets/dental-software-logos/pms/eaglesoft.png" },
  { name: "Open Dental", logo: "/assets/dental-software-logos/pms/opendental.png" },
];

const migrationPartners = [
  { name: "Dentrix", logo: "https://framerusercontent.com/images/FK2PxMGUliSNEptVBpWhWGjhW4.png" },
  { name: "Eaglesoft", logo: "/assets/dental-software-logos/pms/eaglesoft.png" },
  { name: "Open Dental", logo: "/assets/dental-software-logos/pms/opendental.png" },
  { name: "Curve Dental", logo: "/assets/additional-dental-logos/pms/curve_dental.jpg" },
  { name: "Dentrix Ascend", logo: "/assets/additional-dental-logos/pms/dentrix_ascend.png" },
  { name: "Denticon", logo: "/assets/additional-dental-logos/pms/denticon.png" },
  { name: "DentiMax", logo: "/assets/additional-dental-logos/pms/dentimax.webp" },
  { name: "Dolphin" },
];

const migrationHighlights = [
  "Zero-downtime migration — cut over on a weekend",
  "Full data QA — patients, ledgers, notes, images",
  "White-glove onboarding included",
  "Mapping & validation handled by our team",
];

const integrationHighlights = [
  "Server-based PMSs only",
  "Live in 4 weeks — no migration needed",
  "Asha layers on top of your existing PMS",
];

const LogoCard = ({ p, size = "md" }) => {
  const sizes = {
    sm: { padding: "px-3 py-3 min-h-[88px]", imgBox: "h-[36px]", img: "max-h-[36px]", label: "text-[11px]", text: "text-[14px]" },
    md: { padding: "px-3 py-4 min-h-[110px]", imgBox: "h-[44px]", img: "max-h-[44px]", label: "text-[12px]", text: "text-[16px]" },
    lg: { padding: "px-4 py-7 min-h-[170px]", imgBox: "h-[80px]", img: "max-h-[80px]", label: "text-[14px]", text: "text-[22px]" },
  };
  const s = sizes[size];
  return (
    <div
      className={`bg-white rounded-xl border border-slate-200 flex flex-col items-center justify-center gap-2 hover:border-blue-200 hover:shadow-md transition-all ${s.padding}`}
    >
      {p.logo ? (
        <>
          <div className={`flex items-center justify-center ${s.imgBox}`}>
            <img
              src={p.logo}
              alt={p.name}
              className={`${s.img} max-w-[90%] object-contain`}
            />
          </div>
          <p className={`font-semibold text-slate-700 ${s.label}`}>
            {p.name}
          </p>
        </>
      ) : (
        <p className={`font-bold text-slate-700 tracking-tight text-center ${s.text}`}>
          {p.name}
        </p>
      )}
    </div>
  );
};

const MigrationPartnersSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-6 xl:p-10 relative overflow-hidden font-sans">
      {/* Ambient blue gradient blurs */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #93c5fd 50%, #dbeafe 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #bfdbfe 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1200px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <h2 className="text-3xl lg:text-[40px] font-bold text-slate-900 tracking-tight leading-tight">
            Works with{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              your PMS — or replaces it.
            </span>
          </h2>
          <p className="mt-3 text-[14.5px] text-slate-600 font-medium max-w-2xl mx-auto">
            Two paths in: integrate with your existing server-based PMS, or migrate off it onto Asha PMS.
          </p>
        </motion.div>

        {/* Two-column split */}
        <div className="w-full grid grid-cols-2 gap-5">
          {/* LEFT — Integration */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="rounded-2xl border border-slate-200 bg-white shadow-[0_16px_44px_-22px_rgba(15,23,42,0.22)] overflow-hidden flex flex-col"
          >
            <div className="px-6 pt-5 pb-3 border-b border-slate-100">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.16em] text-blue-700 ring-1 ring-inset ring-blue-100">
                <Plug className="h-3 w-3" /> Integration
              </div>
              <p className="mt-2.5 text-[18px] font-semibold text-slate-900 leading-tight">
                Keep your existing PMS
              </p>
              <p className="mt-1 text-[12.5px] text-slate-500 leading-snug">
                We integrate with three server-based systems.
              </p>
            </div>

            <div className="px-5 py-5">
              <div className="grid grid-cols-3 gap-3">
                {integrationPartners.map((p, i) => (
                  <LogoCard key={i} p={p} size="lg" />
                ))}
              </div>
            </div>

            <div className="mt-auto border-t border-slate-100 bg-slate-50/60 px-5 py-3 space-y-1.5">
              {integrationHighlights.map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Check className="mt-[3px] h-3.5 w-3.5 shrink-0 text-blue-500" />
                  <span className="text-[12.5px] font-medium leading-snug text-slate-700">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* RIGHT — Migration */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="rounded-2xl border border-blue-200 ring-1 ring-blue-100 bg-white shadow-[0_24px_60px_-22px_rgba(59,130,246,0.4)] overflow-hidden flex flex-col"
          >
            <div className="px-6 pt-5 pb-3 border-b border-slate-100">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.16em] text-indigo-700 ring-1 ring-inset ring-indigo-100">
                <Rocket className="h-3 w-3" /> Migration
              </div>
              <p className="mt-2.5 text-[18px] font-semibold text-slate-900 leading-tight">
                Migrate from nearly any PMS
              </p>
              <p className="mt-1 text-[12.5px] text-slate-500 leading-snug">
                Our team has moved practices off most legacy systems.
              </p>
            </div>

            <div className="px-5 py-5">
              <div className="grid grid-cols-3 gap-2.5">
                {migrationPartners.map((p, i) => (
                  <LogoCard key={i} p={p} size="sm" />
                ))}
                <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50/70 via-white to-indigo-50/50 px-3 py-3 flex flex-col items-center justify-center gap-1 min-h-[88px]">
                  <Sparkles className="w-4 h-4 text-blue-500" />
                  <span className="text-[13px] font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent leading-tight">
                    & many more
                  </span>
                  <p className="text-[10px] text-slate-500 text-center leading-tight">
                    Oryx, Carestack, Tab32…
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-auto border-t border-slate-100 bg-slate-50/60 px-5 py-3 space-y-1.5">
              {migrationHighlights.map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Check className="mt-[3px] h-3.5 w-3.5 shrink-0 text-indigo-500" />
                  <span className="text-[12.5px] font-medium leading-snug text-slate-700">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <p className="mt-5 text-[13px] text-slate-500 text-center font-medium">
          Don't see your system? Ask us — we support custom migrations on a case-by-case basis.
        </p>
      </div>
    </div>
  );
};

export default MigrationPartnersSlide;
